import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Loader2, Plus, Trash2, CheckCircle2, XCircle, Circle, AlertCircle,
  ListTodo, GitBranch, ArrowRight, Calendar, Save, Eye, Pencil,
} from 'lucide-react';
import { workshopApi } from '../../../services/api';
import Markdown from './Markdown';
import {
  type FeatureDetail, type FeatureStatus, type UatStatus, type UatCase, type BacklogItem,
  FEATURE_STATUS_LABEL, UAT_STATUS_LABEL, BACKLOG_STATUS_LABEL, BACKLOG_PRIORITY_LABEL,
} from '../../../types';

const FEATURE_STATUS_OPTIONS: FeatureStatus[] = ['planned', 'in_progress', 'in_uat', 'shipped', 'cancelled'];

const UAT_STATUS_META: Record<UatStatus, { icon: typeof Circle; color: string }> = {
  not_tested: { icon: Circle,       color: 'text-gray-400 dark:text-gray-500' },
  pass:       { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  fail:       { icon: XCircle,      color: 'text-red-600 dark:text-red-400' },
  blocked:    { icon: AlertCircle,  color: 'text-amber-600 dark:text-amber-400' },
};

export default function FeatureDetailView({ featureId, onBack }: { featureId: string; onBack: () => void }) {
  const [data, setData] = useState<FeatureDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Local drafts for the markdown sections so they don't autosave on every keystroke
  const [problemDraft, setProblemDraft] = useState('');
  const [approachDraft, setApproachDraft] = useState('');
  const [implDraft, setImplDraft] = useState('');
  const [dirty, setDirty] = useState<{ problem: boolean; approach: boolean; impl: boolean }>({ problem: false, approach: false, impl: false });
  const [saving, setSaving] = useState(false);

  // New UAT case form
  const [showNewUat, setShowNewUat] = useState(false);
  const [uatTitle, setUatTitle] = useState('');
  const [uatSteps, setUatSteps] = useState('');
  const [uatExpected, setUatExpected] = useState('');

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await workshopApi.getFeature(featureId);
      setData(detail);
      setProblemDraft(detail.feature.problem_statement ?? '');
      setApproachDraft(detail.feature.approach ?? '');
      setImplDraft(detail.feature.implementation_notes ?? '');
      setDirty({ problem: false, approach: false, impl: false });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feature');
    } finally {
      setLoading(false);
    }
  }, [featureId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSaveDocs = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const patch: Partial<FeatureDetail['feature']> = {};
      if (dirty.problem) patch.problem_statement = problemDraft;
      if (dirty.approach) patch.approach = approachDraft;
      if (dirty.impl) patch.implementation_notes = implDraft;
      const updated = await workshopApi.updateFeature(data.feature.id, patch);
      setData({ ...data, feature: { ...data.feature, ...updated } });
      setDirty({ problem: false, approach: false, impl: false });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: FeatureStatus) => {
    if (!data) return;
    setError(null);
    try {
      const updated = await workshopApi.updateFeature(data.feature.id, { status });
      // Refetch so we get the new lineage event
      await fetchDetail();
      void updated;
    } catch (e: unknown) {
      // The hard UAT gate returns 422 with structured detail — surface it.
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const d = detail as { message?: string; unresolved_cases?: { title: string; status: string }[] };
        const head = d.message ?? 'Cannot change status';
        const list = d.unresolved_cases?.length
          ? `\n• ${d.unresolved_cases.map((c) => `${c.title} (${c.status})`).join('\n• ')}`
          : '';
        setError(head + list);
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed to update status');
    }
  };

  const handleDetachItem = async (item: BacklogItem) => {
    if (!confirm(`Detach "${item.title}" from this feature?`)) return;
    await workshopApi.updateItem(item.id, { feature_id: undefined });
    fetchDetail();
  };

  const handleAddUat = async () => {
    if (!data || !uatTitle.trim()) return;
    await workshopApi.createUat({
      feature_id: data.feature.id,
      title: uatTitle.trim(),
      steps: uatSteps.trim() || undefined,
      expected_result: uatExpected.trim() || undefined,
    });
    setUatTitle(''); setUatSteps(''); setUatExpected('');
    setShowNewUat(false);
    fetchDetail();
  };

  const handleUatStatus = async (uat: UatCase, status: UatStatus) => {
    await workshopApi.updateUat(uat.id, { status });
    fetchDetail();
  };

  const handleUatDelete = async (uat: UatCase) => {
    if (!confirm(`Delete UAT case "${uat.title}"?`)) return;
    await workshopApi.deleteUat(uat.id);
    fetchDetail();
  };

  if (loading) {
    return <div className="p-4 text-sm text-gray-500"><Loader2 size={14} className="animate-spin inline mr-1" />Loading feature…</div>;
  }
  if (!data) {
    return <div className="p-4 text-sm text-red-600">{error ?? 'Feature not found'}</div>;
  }

  const f = data.feature;
  const uatPass = data.uat_cases.filter((u) => u.status === 'pass').length;
  const uatTotal = data.uat_cases.length;
  const anyDirty = dirty.problem || dirty.approach || dirty.impl;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-surface-800 text-gray-500">
          <ArrowLeft size={16} />
        </button>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{f.name}</h2>
        <span className="text-xs font-mono text-gray-400 ml-1">{f.slug}</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Status:</span>
          {FEATURE_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => handleStatusChange(s)}
              className={`px-2 py-1 text-xs rounded font-medium ${
                f.status === s
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-surface-850 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-surface-800'
              }`}
            >
              {FEATURE_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      {f.shipped_at && (
        <div className="mb-3 text-xs text-green-600 dark:text-green-400 inline-flex items-center gap-1">
          <Calendar size={12} /> Shipped {new Date(f.shipped_at).toLocaleString()}
        </div>
      )}

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300 whitespace-pre-line">{error}</div>
      )}

      {/* Documentation sections */}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <DocSection
          label="Problem statement"
          value={problemDraft}
          onChange={(v) => { setProblemDraft(v); setDirty((d) => ({ ...d, problem: true })); }}
          placeholder="What's wrong, who reported it, why it matters."
        />
        <DocSection
          label="Approach"
          value={approachDraft}
          onChange={(v) => { setApproachDraft(v); setDirty((d) => ({ ...d, approach: true })); }}
          placeholder="How we're going to fix it. Architecture, trade-offs, scope."
        />
        <DocSection
          label="Implementation notes"
          value={implDraft}
          onChange={(v) => { setImplDraft(v); setDirty((d) => ({ ...d, impl: true })); }}
          placeholder="What was actually built. Commit refs, files changed, gotchas."
        />
      </div>

      <div className="flex justify-end mb-6">
        <button
          onClick={handleSaveDocs}
          disabled={!anyDirty || saving}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-medium"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : anyDirty ? 'Save docs' : 'Docs saved'}
        </button>
      </div>

      {/* Linked items */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
          <ListTodo size={14} />
          Linked backlog items
          <span className="text-xs font-normal text-gray-500">({data.items.length})</span>
        </h3>
        {data.items.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No items linked. Promote from feedback or assign existing items to this feature.</p>
        ) : (
          <div className="space-y-1">
            {data.items.map((i) => (
              <div key={i.id} className="px-2 py-1.5 border border-gray-200 dark:border-surface-800 rounded text-sm flex items-center gap-2">
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-surface-850 text-gray-700 dark:text-gray-300 font-medium">
                  {BACKLOG_PRIORITY_LABEL[i.priority]}
                </span>
                <span className="flex-1 truncate text-gray-900 dark:text-white">{i.title}</span>
                <span className="text-[10px] text-gray-500">{BACKLOG_STATUS_LABEL[i.status]}</span>
                <button onClick={() => handleDetachItem(i)} className="p-1 text-gray-400 hover:text-red-600" title="Detach from feature">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* UAT cases */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <CheckCircle2 size={14} />
            UAT cases
            <span className="text-xs font-normal text-gray-500">
              ({uatPass}/{uatTotal} passing)
            </span>
          </h3>
          <button
            onClick={() => setShowNewUat(true)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-surface-850 hover:bg-gray-200 dark:hover:bg-surface-800 rounded text-gray-700 dark:text-gray-300"
          >
            <Plus size={12} />
            Add case
          </button>
        </div>

        {showNewUat && (
          <div className="mb-2 p-2 border border-gray-200 dark:border-surface-800 rounded bg-gray-50 dark:bg-surface-850 space-y-2">
            <input type="text" value={uatTitle} onChange={(e) => setUatTitle(e.target.value)} placeholder="Case title (e.g. 'Non-contrast CT head produces no enhancement language')" className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-900 text-gray-900 dark:text-white" />
            <textarea value={uatSteps} onChange={(e) => setUatSteps(e.target.value)} placeholder="Steps (markdown)" rows={3} className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-900 text-gray-900 dark:text-white font-mono" />
            <textarea value={uatExpected} onChange={(e) => setUatExpected(e.target.value)} placeholder="Expected result" rows={2} className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-900 text-gray-900 dark:text-white" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowNewUat(false); setUatTitle(''); setUatSteps(''); setUatExpected(''); }} className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-200 dark:hover:bg-surface-800 rounded">Cancel</button>
              <button onClick={handleAddUat} disabled={!uatTitle.trim()} className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded font-medium">Add case</button>
            </div>
          </div>
        )}

        {data.uat_cases.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No UAT cases yet. Add one above to drive the in_uat → shipped gate.</p>
        ) : (
          <div className="space-y-2">
            {data.uat_cases.map((u) => {
              const meta = UAT_STATUS_META[u.status];
              const Icon = meta.icon;
              return (
                <div key={u.id} className="p-2 border border-gray-200 dark:border-surface-800 rounded">
                  <div className="flex items-start gap-2">
                    <Icon size={16} className={`${meta.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{u.title}</div>
                      {u.steps && <details className="mt-1"><summary className="text-xs text-gray-500 cursor-pointer">Steps</summary><div className="mt-1 p-2 bg-gray-50 dark:bg-surface-850 rounded"><Markdown>{u.steps}</Markdown></div></details>}
                      {u.expected_result && <details className="mt-1"><summary className="text-xs text-gray-500 cursor-pointer">Expected</summary><div className="mt-1 p-2 bg-gray-50 dark:bg-surface-850 rounded"><Markdown>{u.expected_result}</Markdown></div></details>}
                      {u.last_tested_at && <div className="text-[10px] text-gray-400 mt-1">Last tested {new Date(u.last_tested_at).toLocaleString()}</div>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(['pass', 'fail', 'blocked', 'not_tested'] as UatStatus[]).map((s) => (
                        <button
                          key={s}
                          onClick={() => handleUatStatus(u, s)}
                          className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                            u.status === s
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 dark:bg-surface-850 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-surface-800'
                          }`}
                        >
                          {UAT_STATUS_LABEL[s]}
                        </button>
                      ))}
                      <button onClick={() => handleUatDelete(u)} className="p-1 text-gray-400 hover:text-red-600 ml-1" title="Delete UAT case">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Lineage for this feature */}
      <section>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
          <GitBranch size={14} />
          Lineage
        </h3>
        {data.lineage.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No events yet.</p>
        ) : (
          <div className="space-y-1">
            {data.lineage.map((e) => (
              <div key={e.id} className="px-2 py-1 text-xs flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <span className="text-gray-500">{new Date(e.created_at).toLocaleString()}</span>
                <span className="text-gray-700 dark:text-gray-300">{e.entity_type}</span>
                <span className="truncate flex-1 text-gray-900 dark:text-white">{e.entity_label}</span>
                <span className="font-mono">{e.from_state ?? '—'}</span>
                <ArrowRight size={10} className="text-gray-400" />
                <span className="font-mono text-primary-600 dark:text-primary-400">{e.to_state}</span>
                {e.by_user_email && <span className="text-[10px] opacity-60 ml-1">{e.by_user_email}</span>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function DocSection({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  // Default to preview when there's content (read mode), edit when empty
  // (priming the user to write). Manual toggle overrides either way.
  const [mode, setMode] = useState<'preview' | 'edit'>(value ? 'preview' : 'edit');
  return (
    <div className="p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">{label}</label>
        <button
          onClick={() => setMode(mode === 'preview' ? 'edit' : 'preview')}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-850 rounded"
          title={mode === 'preview' ? 'Edit markdown' : 'Preview rendered'}
        >
          {mode === 'preview' ? <Pencil size={10} /> : <Eye size={10} />}
          {mode === 'preview' ? 'Edit' : 'Preview'}
        </button>
      </div>
      {mode === 'edit' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={6}
          autoFocus
          className="w-full text-xs font-mono border-0 focus:outline-none bg-transparent text-gray-900 dark:text-white placeholder-gray-400 resize-y"
        />
      ) : (
        <div onClick={() => setMode('edit')} className="cursor-text min-h-[6rem]" title="Click to edit">
          {value ? <Markdown>{value}</Markdown> : <span className="text-gray-400 dark:text-gray-500 italic text-xs">{placeholder}</span>}
        </div>
      )}
    </div>
  );
}
